/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React from "react";
import PropTypes from "prop-types";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Radio,
  Select,
  TextField,
  Typography,
} from "@mui/material";

const AddMakeModelYearDialog = ({
  open,
  onCloseReset,
  onCancel,
  onSave,
  isLoading,
  isEditMode,
  oemMmyOptions,
  makeModelForm,
  setMakeModelForm,
  makeInputValue,
  setMakeInputValue,
  modelInputValue,
  setModelInputValue,
  makeError,
  setMakeError,
  modelError,
  setModelError,
  yearError,
  setYearError,
  ecuTypeError,
  setEcuTypeError,
  showEcuTypeDropdown,
  setShowEcuTypeDropdown,
  ecuTypeOptions,
  availableModelOptions,
  availableYearOptions,
}) => {
  const [hasChanges, setHasChanges] = React.useState(false);
  
  // Reset hasChanges when dialog closes
  React.useEffect(() => {
    if (!open) {
      setHasChanges(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open || isEditMode) return;
    if (makeModelForm.make || !oemMmyOptions.length) return;

    const defaultMake = oemMmyOptions[0]?.manufacturerName || "";
    if (!defaultMake) return;

    const existingMake = getExistingMake(defaultMake);
    const selectedEcuType = existingMake?.ecuType || "";

    setMakeModelForm({
      ...makeModelForm,
      make: defaultMake,
      ecuType: selectedEcuType,
      model: "",
    });
    setMakeInputValue(defaultMake);
    setModelInputValue("");
    setShowEcuTypeDropdown(!selectedEcuType);
    if (makeError) setMakeError(false);
    if (ecuTypeError && selectedEcuType) setEcuTypeError(false);
  }, [
    open,
    isEditMode,
    makeModelForm,
    oemMmyOptions,
    setMakeModelForm,
    setMakeInputValue,
    setModelInputValue,
    setShowEcuTypeDropdown,
    makeError,
    setMakeError,
    ecuTypeError,
    setEcuTypeError,
  ]);

  const getExistingMake = (makeValue) =>
    oemMmyOptions.find((group) => group.manufacturerName === makeValue);

  return (
    <Dialog
      open={open}
      onClose={onCloseReset}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "8px",
            padding: "8px",
          },
        },
      }}
    >
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.modal + 1 })}
        open={Boolean(isLoading)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          fontSize: "20px",
          fontWeight: 500,
        }}
      >
        {isEditMode ? "Edit Make Model Year" : "Map Make Model Year"}
        <IconButton onClick={onCloseReset} sx={{ padding: "4px" }} disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "0 24px 24px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Make, Model, Year Fields in a Row */}
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* Make Field */}
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  marginBottom: "8px",
                  color: isEditMode ? "#949596" : "#121415",
                }}
              >
                Make <span style={{ color: "#D32F2F" }}>*</span>
              </Typography>
              <Autocomplete
                freeSolo
                fullWidth
                autoSelect={false}
                autoHighlight={false}
                disableClearable={true}
                forcePopupIcon={true}
                popupIcon={<ArrowDropDownIcon />}
                disabled={isEditMode}
                value={makeModelForm.make}
                inputValue={makeInputValue}
                onChange={(event, newValue) => {
                  // Check if selected make exists in oemMmyOptions
                  const existingMake = getExistingMake(newValue);

                  if (existingMake) {
                    const selectedEcuType = existingMake.ecuType || "";
                    // Auto-populate ECU type from existing make
                    setMakeModelForm({
                      ...makeModelForm,
                      make: newValue || "",
                      ecuType: selectedEcuType,
                      model: "", // Reset model when make changes
                    });
                    setShowEcuTypeDropdown(!selectedEcuType); // Hide dropdown if ECU type is available
                    if (ecuTypeError && selectedEcuType) {
                      setEcuTypeError(false);
                    }
                  } else {
                    // New make - keep ECU type empty, show dropdown
                    setMakeModelForm({
                      ...makeModelForm,
                      make: newValue || "",
                      ecuType: "",
                      model: "", // Reset model when make changes
                    });
                    setShowEcuTypeDropdown(true); // Show dropdown for new make
                  }

                  setMakeInputValue(newValue || "");
                  // Clear model input when make changes
                  setModelInputValue("");
                  // Clear error when user selects a value
                  if (makeError && newValue) {
                    setMakeError(false);
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  setMakeInputValue(newInputValue);
                  if (reason === "input") {
                    const existingMake = getExistingMake(newInputValue);

                    if (existingMake) {
                      const selectedEcuType = existingMake.ecuType || "";
                      setMakeModelForm({
                        ...makeModelForm,
                        make: newInputValue || "",
                        ecuType: selectedEcuType,
                        model: "",
                      });
                      setShowEcuTypeDropdown(!selectedEcuType);
                      if (ecuTypeError && selectedEcuType) {
                        setEcuTypeError(false);
                      }
                    } else {
                      setMakeModelForm({
                        ...makeModelForm,
                        make: newInputValue || "",
                        ecuType: "",
                        model: "",
                      });
                      setShowEcuTypeDropdown(true);
                    }
                    // Clear model input when make changes
                    setModelInputValue("");
                  /* istanbul ignore next */
                  } else if (reason === "clear" || reason === "reset") {
                    // If reason is 'clear' or 'reset', clear everything
                    setMakeModelForm({
                      ...makeModelForm,
                      make: "",
                      ecuType: "",
                      model: "",
                    });
                    setModelInputValue("");
                    setShowEcuTypeDropdown(true);
                  }
                  // Clear error when user types
                  if (makeError && newInputValue) {
                    setMakeError(false);
                  }
                }}
                options={oemMmyOptions.map(
                  (group) => group.manufacturerName
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option}>
                    <Radio
                      checked={makeModelForm.make === option}
                      sx={{
                        padding: "0 8px 0 0",
                        "& .MuiSvgIcon-root": {
                          fontSize: 18,
                        },
                      }}
                    />
                    {option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Make"
                    error={makeError}
                    helperText={makeError ? "Make is required" : ""}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "40px",
                        fontSize: "14px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "8px 14px !important",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#717273",
                        opacity: 1,
                      },
                    }}
                  />
                )}
                slotProps={{
                  listbox: {
                    sx: {
                      maxHeight: 200,
                      "& .MuiAutocomplete-option": {
                        minHeight: "32px",
                        fontSize: "14px",
                        padding: "4px 14px",
                      },
                    },
                  },
                }}
              />
            </Box>

            {/* Model Field */}
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  marginBottom: "8px",
                  color: !makeModelForm.make || isEditMode ? "#949596" : "#121415",
                }}
              >
                Model <span style={{ color: "#D32F2F" }}>*</span>
              </Typography>
              <Autocomplete
                freeSolo
                fullWidth
                autoSelect={false}
                autoHighlight={false}
                disableClearable={true}
                forcePopupIcon={true}
                popupIcon={<ArrowDropDownIcon />}
                disabled={!makeModelForm.make || isEditMode}
                value={makeModelForm.model}
                inputValue={modelInputValue}
                onChange={(event, newValue) => {
                  setMakeModelForm({
                    ...makeModelForm,
                    model: newValue || "",
                    year: [], // Reset year when model changes
                  });
                  setModelInputValue(newValue || "");
                  // Clear error when user selects a value
                  if (modelError && newValue) {
                    setModelError(false);
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  // Update input value for display
                  setModelInputValue(newInputValue);
                  // Also update form value for custom/new Model names
                  if (reason === "input") {
                    // When user types, update the form value too
                    setMakeModelForm({
                      ...makeModelForm,
                      model: newInputValue || "",
                      year: [], // Reset year when model changes
                    });
                  /* istanbul ignore next */
                  } else if (reason === "clear" || reason === "reset") {
                    // If reason is 'clear' or 'reset', clear the model
                    setMakeModelForm({
                      ...makeModelForm,
                      model: "",
                      year: [], // Reset year when model is cleared
                    });
                  }
                  // Clear error when user types
                  if (modelError && newInputValue) {
                    setModelError(false);
                  }
                }}
                options={[...availableModelOptions].sort((a, b) => a.localeCompare(b))}
                renderOption={(props, option) => (
                  <li {...props} key={option}>
                    <Radio
                      checked={makeModelForm.model === option}
                      sx={{
                        padding: "0 8px 0 0",
                        "& .MuiSvgIcon-root": {
                          fontSize: 18,
                        },
                      }}
                    />
                    {option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Model"
                    error={modelError}
                    helperText={modelError ? "Model is required" : ""}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "40px",
                        fontSize: "14px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "8px 14px !important",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#717273",
                        opacity: 1,
                      },
                    }}
                  />
                )}
                slotProps={{
                  listbox: {
                    sx: {
                      maxHeight: 200,
                      "& .MuiAutocomplete-option": {
                        minHeight: "32px",
                        fontSize: "14px",
                        padding: "4px 14px",
                      },
                    },
                  },
                }}
              />
            </Box>

            {/* Year Field */}
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  marginBottom: "8px",
                  color: !makeModelForm.model && !isEditMode ? "#949596" : "#121415",
                }}
              >
                Year <span style={{ color: "#D32F2F" }}>*</span>
              </Typography>
              <Select
                fullWidth
                displayEmpty
                multiple
                disabled={!makeModelForm.model && !isEditMode}
                value={makeModelForm.year}
                onChange={(e) => {
                  const value = e.target.value;
                  setMakeModelForm({
                    ...makeModelForm,
                    year:
                      typeof value === "string" ? value.split(",") : value,
                  });
                  // Track changes in edit mode
                  if (isEditMode) {
                    setHasChanges(true);
                  }
                  // Clear error when user selects a value
                  if (yearError && value.length > 0) {
                    setYearError(false);
                  }
                }}
                error={yearError}
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return <span style={{ color: "#717273" }}>Select Year</span>;
                  }
                  const displayYears = selected.slice(0, 3);
                  return displayYears.join(", ");
                }}
                sx={{
                  height: "40px",
                  fontSize: "14px",
                  "& .MuiSelect-select": {
                    padding: "8px 14px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                MenuProps={{
                  slotProps: {
                    paper: {
                      sx: {
                        maxHeight: 200,
                        "& .MuiMenuItem-root": {
                          minHeight: "32px",
                          fontSize: "14px",
                          padding: "4px 14px",
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Select Year
                </MenuItem>
                {(() => {
                  // Merge availableYearOptions with selected years to ensure all are available
                  // Convert all to strings and deduplicate
                  const yearsSet = new Set();
                  
                  // Add available years
                  if (availableYearOptions?.length > 0) {
                    availableYearOptions.forEach(year => yearsSet.add(String(year)));
                  }
                  
                  // Add selected years
                  if (makeModelForm.year?.length > 0) {
                    makeModelForm.year.forEach(year => yearsSet.add(String(year)));
                  }
                  
                  const allYears = Array.from(yearsSet).sort((a, b) => a - b);
                  
                  return allYears?.length > 0
                    ? allYears.map((year) => (
                        <MenuItem key={year} value={year}>
                          <Checkbox
                            checked={makeModelForm.year.map(String).includes(year)}
                            sx={{
                              padding: "0 8px 0 0",
                              "& .MuiSvgIcon-root": {
                                fontSize: 18,
                              },
                            }}
                          />
                          {year}
                        </MenuItem>
                      ))
                    : null;
                })()}
              </Select>
              {yearError && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "0.75rem",
                    marginTop: "4px",
                    marginLeft: "14px",
                  }}
                >
                  Year is required
                </Typography>
              )}
            </Box>
          </Box>

          {/* ECU Type Mapping Field */}
          <Box>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                marginBottom: "8px",
                color: isEditMode ? "#949596" : "#121415",
              }}
            >
              Map ECU Type <span style={{ color: "#D32F2F" }}>*</span>
            </Typography>

            {/* Show chip if ECU type is selected and dropdown is hidden */}
            {!showEcuTypeDropdown && makeModelForm.ecuType ? (
              <Chip
                label={makeModelForm.ecuType}
                onClick={() => {
                  const existingMake = getExistingMake(makeModelForm.make);
                  if (!isEditMode && !existingMake) {
                    setShowEcuTypeDropdown(true);
                  }
                }}
                sx={{
                  height: "28px",
                  fontSize: "14px",
                  fontWeight: 400,
                  backgroundColor: "#E8E8E8",
                  color: "#000000DE",
                  border: "1px solid #12141514",
                  borderRadius: "4px",
                  cursor: (() => {
                    const existingMake = getExistingMake(makeModelForm.make);
                    return isEditMode || existingMake ? "not-allowed" : "pointer";
                  })(),
                  opacity: (() => {
                    const existingMake = getExistingMake(makeModelForm.make);
                    return isEditMode || existingMake ? 0.6 : 1;
                  })(),
                  "& .MuiChip-label": {
                    padding: "0 12px",
                  },
                  "&:hover": {
                    backgroundColor: (() => {
                      const existingMake = getExistingMake(makeModelForm.make);
                      return isEditMode || existingMake ? "#E8E8E8" : "#D8D8D8";
                    })(),
                  },
                }}
              />
            ) : (
              /* Show dropdown if showEcuTypeDropdown is true or ECU type not selected */
              <Select
                displayEmpty
                disabled={isEditMode}
                value={makeModelForm.ecuType}
                onChange={(e) => {
                  setMakeModelForm({
                    ...makeModelForm,
                    ecuType: e.target.value,
                    // Don't reset make and model - user is associating new make with ECU type
                  });
                  // Clear error when user selects a value
                  if (ecuTypeError && e.target.value) {
                    setEcuTypeError(false);
                  }
                }}
                error={ecuTypeError}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: "#717273" }}>Select ECU Type</span>;
                  }
                  return selected;
                }}
                sx={{
                  width: "calc((100% - 16px) / 3)",
                  height: "40px",
                  fontSize: "14px",
                  "& .MuiSelect-select": {
                    padding: "8px 14px",
                  },
                }}
                MenuProps={{
                  slotProps: {
                    paper: {
                      sx: {
                        maxHeight: 200,
                        "& .MuiMenuItem-root": {
                          minHeight: "32px",
                          fontSize: "14px",
                          padding: "4px 14px",
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Select ECU Type
                </MenuItem>
                {ecuTypeOptions?.length > 0
                  ? ecuTypeOptions.map((ecuType) => (
                      <MenuItem key={ecuType} value={ecuType}>
                        <Radio
                          checked={makeModelForm.ecuType === ecuType}
                          sx={{
                            padding: "0 8px 0 0",
                            "& .MuiSvgIcon-root": {
                              fontSize: 18,
                            },
                          }}
                        />
                        {ecuType}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            )}
            {ecuTypeError && (
              <Typography
                sx={{
                  color: "#d32f2f",
                  fontSize: "0.75rem",
                  marginTop: "4px",
                  marginLeft: "14px",
                }}
              >
                ECU Type is required
              </Typography>
            )}
          </Box>
          
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          padding: 0,
          paddingTop: "24px",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button
          onClick={onCancel}
          variant="outlined"
          disabled={isLoading}
          sx={{
            textTransform: "none",
            padding: "8px 24px",
            fontSize: "14px",
            fontWeight: 500,
            height: "36px",
            color: "#666666",
            borderColor: "#E0E0E0",
            backgroundColor: "#F5F5F5",
            "&:hover": {
              backgroundColor: "#EEEEEE",
              borderColor: "#E0E0E0",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={
            isLoading ||
            (isEditMode
              ? !hasChanges || makeModelForm.year.length === 0 // In edit mode: needs changes AND at least one year
              : !makeModelForm.make ||
                !makeModelForm.model ||
                !makeModelForm.year ||
                makeModelForm.year.length === 0 ||
                !makeModelForm.ecuType)
          }
          sx={{
            textTransform: "none",
            padding: "8px 24px",
            fontSize: "14px",
            fontWeight: 500,
            height: "36px",
            backgroundColor: "#0476E2",
            "&:hover": {
              backgroundColor: "#0356a6",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E0E0E0",
              color: "#9E9E9E",
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddMakeModelYearDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onCloseReset: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isEditMode: PropTypes.bool,
  oemMmyOptions: PropTypes.arrayOf(
    PropTypes.shape({
      manufacturerName: PropTypes.string,
      ecuType: PropTypes.string,
    })
  ).isRequired,
  makeModelForm: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.arrayOf(PropTypes.string),
    ecuType: PropTypes.string,
  }).isRequired,
  setMakeModelForm: PropTypes.func.isRequired,
  makeInputValue: PropTypes.string,
  setMakeInputValue: PropTypes.func.isRequired,
  modelInputValue: PropTypes.string,
  setModelInputValue: PropTypes.func.isRequired,
  makeError: PropTypes.bool,
  setMakeError: PropTypes.func.isRequired,
  modelError: PropTypes.bool,
  setModelError: PropTypes.func.isRequired,
  yearError: PropTypes.bool,
  setYearError: PropTypes.func.isRequired,
  ecuTypeError: PropTypes.bool,
  setEcuTypeError: PropTypes.func.isRequired,
  showEcuTypeDropdown: PropTypes.bool,
  setShowEcuTypeDropdown: PropTypes.func.isRequired,
  ecuTypeOptions: PropTypes.arrayOf(PropTypes.string),
  availableModelOptions: PropTypes.arrayOf(PropTypes.string),
  availableYearOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
};

export default AddMakeModelYearDialog;
